import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Quotation, CustomTabGroup } from './types';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { 
  FileText, 
  Trash, 
  Plus, 
  GripVertical, 
  ChevronDown, 
  ChevronRight,
  ImagePlus,
  Image,
  Edit,
  ChevronUp,
  ChevronLeft,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { useToast } from '@/hooks/use-toast';
import { Textarea } from '@/components/ui/textarea';
import EmbedRenderer from '@/components/ui/EmbedRenderer';

interface CustomTabProps {
  quotation: Quotation;
  handleChange?: (field: keyof Quotation, value: any) => void;
  isEditing?: boolean;
}

// Componente de carrossel de imagens
function ImageCarousel({ images }: { images: string[] }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  if (!images || images.length === 0) return null;
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  return (
    <div className="relative w-full max-w-3xl mx-auto my-4">
      <div className="relative aspect-[16/9] overflow-hidden rounded-lg">
        <img 
          src={images[currentImageIndex]} 
          alt="Imagem do carrossel" 
          className="w-full h-full object-contain"
        />
        
        {images.length > 1 && (
          <>
            <button 
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all"
              aria-label="Imagem anterior"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button 
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all"
              aria-label="Próxima imagem"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentImageIndex ? "bg-white" : "bg-white/50"
                  }`}
                  aria-label={`Ir para imagem ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function CustomTab({ quotation, handleChange, isEditing = false }: CustomTabProps) {
  const { toast } = useToast();
  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // Efeito para inicializar o estado colapsado e garantir que grupos estejam definidos
  useEffect(() => {
    // Garantir que customTabGroups esteja inicializado se handleChange existir
    if (handleChange && !quotation.customTabGroups) {
      handleChange('customTabGroups', []);
    }

    // Inicializar todos os grupos como colapsados por padrão no modo de visualização
    if (!isEditing && quotation.customTabGroups) {
      const initialCollapsedState = quotation.customTabGroups.reduce((acc, group) => {
        acc[group.id] = true; // Começa colapsado
        return acc;
      }, {} as Record<string, boolean>);
      setCollapsedGroups(initialCollapsedState);
    }
  }, [quotation.customTabGroups, isEditing, handleChange]); // Dependências atualizadas

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroupIds(prev => 
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups(prev => {
      const isCurrentlyCollapsed = prev[groupId];
      // Se estiver abrindo um grupo (estava colapsado)
      if (isCurrentlyCollapsed) {
        // Cria um novo estado onde todos estão colapsados
        const newState = Object.keys(prev).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        // Expande apenas o grupo clicado
        newState[groupId] = false;
        return newState;
      } else {
        // Se estiver fechando um grupo (estava expandido), apenas fecha ele
        return {
          ...prev,
          [groupId]: true
        };
      }
    });
  };

  const handleAddGroup = () => {
    if (!handleChange) return;

    const newGroup: CustomTabGroup = {
      id: uuidv4(),
      title: 'Novo Grupo',
      content: '',
      order: (quotation.customTabGroups?.length || 0),
      images: []
    };

    handleChange('customTabGroups', [...(quotation.customTabGroups || []), newGroup]);
    setExpandedGroupIds(prev => [...prev, newGroup.id]);
  };

  const handleUpdateGroup = (groupId: string, field: keyof CustomTabGroup, value: any) => {
    if (!handleChange || !quotation.customTabGroups) return;

    const updatedGroups = quotation.customTabGroups.map(group => 
      group.id === groupId ? { ...group, [field]: value } : group
    );

    handleChange('customTabGroups', updatedGroups);
  };

  const handleDeleteGroup = (groupId: string) => {
    if (!handleChange || !quotation.customTabGroups) return;

    const updatedGroups = quotation.customTabGroups.filter(group => group.id !== groupId);
    handleChange('customTabGroups', updatedGroups);
    setExpandedGroupIds(prev => prev.filter(id => id !== groupId));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !handleChange || !quotation.customTabGroups) return;

    const items = Array.from(quotation.customTabGroups);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Atualizar a ordem dos itens
    const updatedGroups = items.map((group, index) => ({
      ...group,
      order: index
    }));

    handleChange('customTabGroups', updatedGroups);
  };

  const openImageManager = (groupId: string) => {
    setCurrentGroupId(groupId);
    setIsImageManagerOpen(true);
  };

  const handleImagesChange = (images: string[]) => {
    if (!currentGroupId || !handleChange || !quotation.customTabGroups) return;

    const updatedGroups = quotation.customTabGroups.map(group => 
      group.id === currentGroupId ? { ...group, images } : group
    );

    handleChange('customTabGroups', updatedGroups);
    toast({
      title: "Imagens atualizadas",
      description: "As imagens do grupo foram atualizadas com sucesso."
    });
  };

  const handleAddEmbedSnippet = (groupId: string) => {
    if (!handleChange || !quotation.customTabGroups) return;
    const updatedGroups = quotation.customTabGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          embedSnippets: [...(group.embedSnippets || []), '']
        };
      }
      return group;
    });
    handleChange('customTabGroups', updatedGroups);
  };

  const handleUpdateEmbedSnippet = (groupId: string, snippetIndex: number, value: string) => {
    if (!handleChange || !quotation.customTabGroups) return;
    const updatedGroups = quotation.customTabGroups.map(group => {
      if (group.id === groupId) {
        const updatedSnippets = [...(group.embedSnippets || [])];
        updatedSnippets[snippetIndex] = value;
        return { ...group, embedSnippets: updatedSnippets };
      }
      return group;
    });
    handleChange('customTabGroups', updatedGroups);
  };

  const handleRemoveEmbedSnippet = (groupId: string, snippetIndex: number) => {
    if (!handleChange || !quotation.customTabGroups) return;
    const updatedGroups = quotation.customTabGroups.map(group => {
      if (group.id === groupId) {
        const updatedSnippets = (group.embedSnippets || []).filter((_, index) => index !== snippetIndex);
        return { ...group, embedSnippets: updatedSnippets };
      }
      return group;
    });
    handleChange('customTabGroups', updatedGroups);
  };

  const renderGroups = () => {
    const groups = quotation.customTabGroups || [];
    
    if (groups.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border border-dashed">
          <FileText className="h-10 w-10 text-gray-300 mb-2" />
          <p className="text-gray-500 mb-4">Nenhum grupo adicionado</p>
          {isEditing && (
            <Button onClick={handleAddGroup}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Grupo
            </Button>
          )}
        </div>
      );
    }
    
    if (isEditing) {
      return (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="groups" type="group">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-3"
              >
                {groups.map((group, index) => (
                  <Draggable
                    key={group.id}
                    draggableId={group.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={cn(
                          "rounded-lg border bg-card text-card-foreground shadow-sm",
                          snapshot.isDragging && "shadow-lg"
                        )}
                      >
                        <div className="flex items-center p-4">
                          <div
                            {...provided.dragHandleProps}
                            className="flex items-center mr-2"
                          >
                            <GripVertical className="h-5 w-5 text-gray-400" />
                          </div>

                          {editingGroupId === group.id ? (
                            <Input
                              value={newGroupTitle || group.title}
                              onChange={(e) => setNewGroupTitle(e.target.value)}
                              onBlur={() => {
                                handleUpdateGroup(group.id, 'title', newGroupTitle);
                                setEditingGroupId(null);
                                setNewGroupTitle('');
                              }}
                              className="flex-1 mr-2"
                              autoFocus
                            />
                          ) : (
                            <div
                              className="flex-1 cursor-pointer"
                              onClick={() => toggleGroupExpansion(group.id)}
                            >
                              <div className="font-medium flex items-center">
                                {expandedGroupIds.includes(group.id) ? (
                                  <ChevronDown className="h-4 w-4 mr-1" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 mr-1" />
                                )}
                                {group.title}
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingGroupId(group.id);
                                setNewGroupTitle(group.title);
                              }}
                            >
                              <Edit className="h-4 w-4 text-gray-500" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => openImageManager(group.id)}
                            >
                              <ImagePlus className="h-4 w-4 text-gray-500" />
                              {group.images && group.images.length > 0 && (
                                <span className="ml-1 text-xs">{group.images.length}</span>
                              )}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteGroup(group.id)}
                            >
                              <Trash className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>

                        {expandedGroupIds.includes(group.id) && (
                          <div className="p-4 pt-0 border-t mt-2">
                            <div className="space-y-4">
                              {/* Rich Text Editor */}
                              <div className="mt-4">
                                <RichTextEditor
                                  value={group.content || ''}
                                  onChange={(value) => handleUpdateGroup(group.id, 'content', value)}
                                  placeholder="Adicione conteúdo a este grupo..."
                                />
                              </div>

                              {/* Exibição de imagens */}
                              {group.images && group.images.length > 0 && (
                                <div className="mt-4">
                                  <div className="text-sm font-medium mb-2">Imagens</div>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {group.images.map((image, imageIndex) => (
                                      <div 
                                        key={`image-${imageIndex}`}
                                        className="relative aspect-square overflow-hidden rounded-md border bg-muted"
                                      >
                                        <img 
                                          src={image} 
                                          alt={`Imagem ${imageIndex + 1}`}
                                          className="h-full w-full object-contain"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Seção para Embed Snippets */}
                              <div className="mt-6">
                                <h4 className="text-sm font-medium mb-2">Conteúdo Embutido (Embed)</h4>
                                
                                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-3">
                                  <div className="flex items-start">
                                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
                                    <div>
                                      <p className="text-sm font-medium text-yellow-800">Atenção: Risco de Segurança</p>
                                      <p className="text-xs text-yellow-700 mt-1">
                                        Colar código HTML/script de fontes não confiáveis pode comprometer a segurança.
                                        Use apenas snippets de fontes conhecidas e seguras.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="space-y-3">
                                  {(group.embedSnippets || []).map((snippet, snipIndex) => (
                                    <div key={snipIndex} className="flex items-start gap-2">
                                      <Textarea
                                        value={snippet}
                                        onChange={(e) => handleUpdateEmbedSnippet(group.id, snipIndex, e.target.value)}
                                        placeholder={`Cole aqui o snippet HTML/script ${snipIndex + 1}`}
                                        rows={4}
                                        className="flex-1 font-mono text-xs"
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveEmbedSnippet(group.id, snipIndex)}
                                        className="mt-1"
                                      >
                                        <Trash className="h-4 w-4 text-red-500" />
                                      </Button>
                                    </div>
                                  ))}
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleAddEmbedSnippet(group.id)}
                                    className="mt-2"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Adicionar Embed
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      );
    } else {
      // Modo de visualização (não-editável) - Utiliza a nova lógica de collapsedGroups
      return (
        <div className="p-3 sm:p-6 lg:p-8">
          <div className="space-y-8">
            {groups.map((group) => {
              // Usar !! para garantir booleano, embora a inicialização já faça isso
              const isCollapsed = !!collapsedGroups[group.id]; 
              
              return (
                <div key={group.id} className="border rounded-lg overflow-hidden shadow-sm">
                  <div 
                    className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                    onClick={() => toggleGroupCollapse(group.id)} // Usa a nova função
                  >
                    <h2 className="text-xl font-medium text-voah-teal">
                      {group.title}
                    </h2>
                    <button className="text-gray-500 hover:text-gray-700 focus:outline-none">
                      {isCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                    </button>
                  </div>
                  
                  {/* A lógica de transição e conteúdo permanece a mesma, dependendo de isCollapsed */}
                  <div className={`transition-all duration-300 ease-in-out ${
                    isCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[5000px] opacity-100' // Adicionado ease-in-out
                  }`}>
                    <div className="p-4 bg-white">
                      {/* Conteúdo Rich Text */}
                      {group.content && (
                        <div 
                          className="prose prose-sm mb-6 text-gray-700 max-w-none" 
                          dangerouslySetInnerHTML={{ __html: group.content }} 
                        />
                      )}
                      
                      {/* Imagens em formato de carrossel */}
                      {group.images && group.images.length > 0 && (
                        <ImageCarousel images={group.images} />
                      )}

                      {/* Renderizar Snippets Embedados */}
                      {(group.embedSnippets || []).map((snippet, index) => (
                        snippet.trim() && <EmbedRenderer key={`${group.id}-embed-${index}`} snippet={snippet} />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  };

  return (
    <div className={isEditing ? "space-y-6" : ""}>
      {isEditing && (
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            Conteúdo da Aba Personalizada
          </h2>
          <Button onClick={handleAddGroup}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Grupo
          </Button>
        </div>
      )}

      {renderGroups()}

      {isEditing && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={quotation.customTabGroups?.find(g => g.id === currentGroupId)?.images || []}
          onImagesChange={handleImagesChange}
          contextId={currentGroupId || 'custom-tab'}
          contextType="customSection"
        />
      )}
    </div>
  );
}

// View-only version for client display
export function CustomTabView({ quotation }: { quotation: Quotation }) {
  // Reutilizando o componente principal no modo não-editável
  return <CustomTab quotation={quotation} isEditing={false} />;
} 